import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { formatDMY } from "@/lib/dateFormat";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarIcon, CalendarClock, ExternalLink, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import HomeworkWall from "@/components/HomeworkWall";

export default function MyHomework() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("all");
  const [showCompleted, setShowCompleted] = useState(true);
  const [wallHw, setWallHw] = useState(null);

  const load = async () => {
    const [hw, gs] = await Promise.all([
      api.get("/homework"),
      api.get("/groups"),
    ]);
    setItems(hw.data);
    setGroups(gs.data);
  };
  useEffect(() => { load(); }, []);

  const groupsMap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const filtered = items.filter((h) => {
    if (groupId !== "all" && h.group_id !== groupId) return false;
    if (!showCompleted && h.completed_at) return false;
    return true;
  });
  const total = items.length;
  const done = items.filter((h) => h.completed_at).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const toggleComplete = async (hw) => {
    try {
      if (hw.completed_at) {
        await api.delete(`/homework/${hw.id}/complete`);
      } else {
        await api.post(`/homework/${hw.id}/complete`);
      }
      load();
    } catch { toast.error(t("error_generic")); }
  };

  return (
    <div>
      <PageHeader title={t("my_homework")} testId="my-homework-header" />

      {total > 0 && (
        <Card className="p-4 mb-5" data-testid="my-homework-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">{t("homework_progress")}</span>
            <span className="text-sm font-semibold text-slate-900">{done}/{total} · {pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : pct >= 50 ? "bg-brand-blue" : "bg-orange-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 max-w-2xl">
        <div>
          <Label className="text-xs">{t("group")}</Label>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="my-homework-group-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer h-10">
            <Checkbox
              checked={showCompleted}
              onCheckedChange={setShowCompleted}
              data-testid="my-homework-show-completed"
            />
            <span className="text-sm text-slate-700">{t("show_completed")}</span>
          </label>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500">{t("no_data")}</div>
        )}
        {filtered.map((hw, idx) => {
          const completed = !!hw.completed_at;
          const unread = hw.unread_channel_count || 0;
          return (
            <div
              key={hw.id}
              className={`flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors ${idx !== 0 ? "border-t border-slate-100" : ""} ${completed ? "opacity-60" : ""}`}
              data-testid={`my-homework-item-${hw.id}`}
            >
              <Checkbox
                checked={completed}
                onCheckedChange={() => toggleComplete(hw)}
                data-testid={`my-homework-toggle-${hw.id}`}
                className="shrink-0"
              />
              <span className="text-[11px] text-slate-500 whitespace-nowrap hidden sm:flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" /> {hw.date}
              </span>
              {hw.due_date && (
                <span className="text-[11px] text-orange-700 whitespace-nowrap hidden md:flex items-center gap-1 font-medium">
                  <CalendarClock className="h-3 w-3" /> {formatDMY(hw.due_date, lang)}
                </span>
              )}
              <span className="text-[11px] text-slate-500 whitespace-nowrap hidden lg:inline truncate max-w-[140px]">
                {groupsMap[hw.group_id]?.name || hw.group_id}
              </span>
              <span className={`flex-1 text-sm truncate ${completed ? "line-through text-slate-500" : "font-medium text-slate-900"}`}>
                {hw.title}
              </span>
              {completed && (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" title={t("completed")} />
              )}
              <button
                type="button"
                onClick={() => setWallHw(hw)}
                className="relative inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 text-slate-600 shrink-0"
                title={t("attachments_and_responses")}
                data-testid={`open-wall-${hw.id}`}
              >
                <Eye className="h-4 w-4" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-emerald-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <Dialog open={!!wallHw} onOpenChange={(o) => !o && setWallHw(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="homework-dialog-title">
              {wallHw?.group_name ? `${wallHw.group_name} — ${wallHw?.title}` : wallHw?.title}
            </DialogTitle>
          </DialogHeader>
          {wallHw && <HomeworkWall homework={wallHw} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
